import { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Heading,
  Card,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Badge,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
  useColorModeValue,
  HStack,
  SimpleGrid,
  TableContainer,
  Skeleton,
  SkeletonText,
  Alert,
  AlertIcon,
  useToast,
  useDisclosure,
  Code,
  Tooltip,
  Portal,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEllipsisV,
  faEdit,
  faTrash,
  faChartBar,
  faCopy,
  faCheck,
  faExternalLinkAlt,
  faLink,
} from '@fortawesome/free-solid-svg-icons';
import { useLinks, useLinkMutations } from '../hooks/useLinks';
import LinkModal from '../components/links/LinkModal';
import LinkStatsModal from '../components/links/LinkStatsModal';
import DeleteConfirmModal from '../components/common/DeleteConfirmModal';
import { useLicenseProtection } from '../hooks/useLicenseProtection';
import { formatFullNumber } from '../utils/format';
import DateRangePicker from '../components/common/DateRangePicker';
import { useRecoilState } from 'recoil';
import { dateRangeAtom } from '../state/dateRangeAtom';
import { parseDateRange } from '../hooks/useAnalytics';

function LinkCard({ link, onViewStats, onEdit, onCopy, onDelete, copied, copiedId, isReadOnly }) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const codeBg = useColorModeValue('gray.50', 'gray.700');
  const serverUrl = window.location.origin.replace(':3058', ':3059');

  return (
    <Card
      bg={cardBg}
      border="1px solid"
      borderColor={borderColor}
      _hover={{ transform: 'translateY(-1px)' }}
      transition="all 0.2s"
    >
      <CardBody>
        <Flex justify="space-between" align="start" mb={3}>
          <Box flex={1}>
            <Text fontWeight="600" noOfLines={1}>{link.link_name}</Text>
            <Text fontSize="xs" color={textSecondary} noOfLines={1} mt={1}>
              {link.destination_url}
            </Text>
          </Box>
          <Menu>
            <MenuButton
              as={IconButton}
              icon={<FontAwesomeIcon icon={faEllipsisV} />}
              variant="ghost"
              size="sm"
              borderRadius="lg"
            />
            <Portal>
              <MenuList zIndex={9999} py={2} borderRadius="xl">
                <MenuItem icon={<FontAwesomeIcon icon={faChartBar} />} onClick={() => onViewStats(link)} mx={2} borderRadius="lg" w="calc(100% - 16px)">
                  View Stats
                </MenuItem>
                <MenuItem
                  icon={<FontAwesomeIcon icon={faEdit} />}
                  onClick={() => onEdit(link)}
                  isDisabled={isReadOnly}
                  mx={2}
                  borderRadius="lg"
                  w="calc(100% - 16px)"
                >
                  Edit
                </MenuItem>
                <MenuItem
                  icon={<FontAwesomeIcon icon={faTrash} />}
                  color={isReadOnly ? 'gray.400' : 'red.500'}
                  onClick={() => onDelete(link)}
                  isDisabled={isReadOnly}
                  mx={2}
                  borderRadius="lg"
                  w="calc(100% - 16px)"
                >
                  Delete
                </MenuItem>
              </MenuList>
            </Portal>
          </Menu>
        </Flex>

        <Box p={2} bg={codeBg} borderRadius="lg" mb={3}>
          <HStack justify="space-between">
            <Code fontSize="xs" bg="transparent">
              {serverUrl}/l/{link.short_code}
            </Code>
            <IconButton
              icon={<FontAwesomeIcon icon={copied && copiedId === link.link_id ? faCheck : faCopy} />}
              size="xs"
              colorScheme={copied && copiedId === link.link_id ? 'green' : 'gray'}
              variant="ghost"
              onClick={() => onCopy(link)}
              aria-label="Copy URL"
            />
          </HStack>
        </Box>

        <SimpleGrid columns={2} spacing={3}>
          <Box>
            <Text fontSize="xs" color={textSecondary} textTransform="uppercase">Clicks</Text>
            <Text fontWeight="600">{formatFullNumber(link.total_clicks || 0)}</Text>
          </Box>
          <Box>
            <Text fontSize="xs" color={textSecondary} textTransform="uppercase">Created</Text>
            <Text fontWeight="500" fontSize="sm">
              {new Date(link.created_at).toLocaleDateString()}
            </Text>
          </Box>
        </SimpleGrid>

        {(link.utm_source || link.utm_medium || link.utm_campaign) && (
          <HStack spacing={1} mt={3} flexWrap="wrap">
            {link.utm_source && (
              <Badge size="sm" colorScheme="blue" borderRadius="full" fontSize="xs">
                {link.utm_source}
              </Badge>
            )}
            {link.utm_medium && (
              <Badge size="sm" colorScheme="purple" borderRadius="full" fontSize="xs">
                {link.utm_medium}
              </Badge>
            )}
            {link.utm_campaign && (
              <Badge size="sm" colorScheme="green" borderRadius="full" fontSize="xs">
                {link.utm_campaign}
              </Badge>
            )}
          </HStack>
        )}
      </CardBody>
    </Card>
  );
}

function LinksPage() {
  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const emptyIconBg = useColorModeValue('brand.50', 'brand.900');
  const toast = useToast();
  const serverUrl = window.location.origin.replace(':3058', ':3059');
  const { isReadOnly, protectAction } = useLicenseProtection();

  // Modal states
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isStatsOpen, onOpen: onStatsOpen, onClose: onStatsClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  const [selectedLink, setSelectedLink] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  // Fetch links from API
  // Date range state (shared globally across all tabs)
  const [dateRange, setDateRange] = useRecoilState(dateRangeAtom);
  const handleDateRangeChange = useCallback((range) => setDateRange(range), [setDateRange]);

  const apiFilters = useMemo(() => {
    const dates = parseDateRange(dateRange);
    return { start: dates.start.toISOString(), end: dates.end.toISOString() };
  }, [dateRange]);

  const { data: links, loading, error, refetch } = useLinks(apiFilters);
  const { createLink, updateLink, deleteLink } = useLinkMutations();

  // Handlers
  const handleViewStats = (link) => {
    setSelectedLink(link);
    onStatsOpen();
  };

  const handleEdit = (link) => {
    setSelectedLink(link);
    onEditOpen();
  };

  const handleCopy = (link) => {
    const shortUrl = `${serverUrl}/l/${link.short_code}`;
    navigator.clipboard.writeText(shortUrl);
    setCopied(true);
    setCopiedId(link.link_id);
    toast({
      title: 'Link copied!',
      status: 'success',
      duration: 2000,
    });
    setTimeout(() => {
      setCopied(false);
      setCopiedId(null);
    }, 2000);
  };

  const handleDeleteClick = (link) => {
    setSelectedLink(link);
    onDeleteOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedLink) return;
    setIsSubmitting(true);
    try {
      await deleteLink(selectedLink.link_id);
      toast({
        title: 'Link deleted',
        status: 'success',
        duration: 3000,
      });
      onDeleteClose();
      refetch();
    } catch (err) {
      toast({
        title: 'Error deleting link',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await createLink({
        name: data.name,
        destinationUrl: data.destinationUrl,
        utmSource: data.utmSource,
        utmMedium: data.utmMedium,
        utmCampaign: data.utmCampaign,
        utmTerm: data.utmTerm,
        utmContent: data.utmContent,
      });
      toast({
        title: 'Link created',
        status: 'success',
        duration: 3000,
      });
      onCreateClose();
      refetch();
    } catch (err) {
      toast({
        title: 'Error creating link',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async (data) => {
    if (!selectedLink) return;
    setIsSubmitting(true);
    try {
      await updateLink(selectedLink.link_id, {
        name: data.name,
        destinationUrl: data.destinationUrl,
        utmSource: data.utmSource,
        utmMedium: data.utmMedium,
        utmCampaign: data.utmCampaign,
        utmTerm: data.utmTerm,
        utmContent: data.utmContent,
      });
      toast({
        title: 'Link updated',
        status: 'success',
        duration: 3000,
      });
      onEditClose();
      refetch();
    } catch (err) {
      toast({
        title: 'Error updating link',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const linksList = links || [];

  return (
    <Box>
      {/* Page Header */}
      <Flex
        direction={{ base: 'column', md: 'row' }}
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
        mb={{ base: 6, md: 8 }}
        gap={4}
      >
        <Box>
          <Heading
            size={{ base: 'lg', md: 'xl' }}
            bgGradient="linear(to-r, brand.500, accent.500)"
            bgClip="text"
            fontWeight="700"
          >
            Link Builder
          </Heading>
          <Text color={textSecondary} mt={1}>
            Create trackable UTM links for marketing campaigns
          </Text>
        </Box>
        <HStack spacing={3}>
          <DateRangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            showCompare={false}
            size="sm"
          />
          <Button
            leftIcon={<FontAwesomeIcon icon={faPlus} />}
            colorScheme="brand"
            size={{ base: 'sm', md: 'md' }}
            borderRadius="xl"
            onClick={() => protectAction(onCreateOpen)}
            isDisabled={isReadOnly}
            title={isReadOnly ? 'License required to create links' : undefined}
          >
            New Link
          </Button>
        </HStack>
      </Flex>

      {/* Error State */}
      {error && (
        <Alert status="error" borderRadius="xl" mb={6}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <>
          {/* Mobile Loading */}
          <SimpleGrid
            columns={{ base: 1, sm: 2, lg: 3, xl: 4 }}
            spacing={4}
            display={{ base: 'grid', lg: 'none' }}
          >
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} bg={cardBg} border="1px solid" borderColor={borderColor}>
                <CardBody>
                  <Skeleton height="20px" mb={2} />
                  <Skeleton height="14px" width="60%" mb={4} />
                  <SkeletonText noOfLines={3} spacing={2} />
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>

          {/* Desktop Loading */}
          <Card bg={cardBg} border="1px solid" borderColor={borderColor} display={{ base: 'none', lg: 'block' }}>
            <CardBody>
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} height="50px" mb={3} borderRadius="md" />
              ))}
            </CardBody>
          </Card>
        </>
      ) : linksList.length > 0 ? (
        <>
          {/* Mobile Card View */}
          <SimpleGrid
            columns={{ base: 1, sm: 2, lg: 3, xl: 4 }}
            spacing={4}
            display={{ base: 'grid', lg: 'none' }}
          >
            {linksList.map((link) => (
              <LinkCard
                key={link.link_id}
                link={link}
                onViewStats={handleViewStats}
                onEdit={(l) => protectAction(() => handleEdit(l))}
                onCopy={handleCopy}
                onDelete={(l) => protectAction(() => handleDeleteClick(l))}
                copied={copied}
                copiedId={copiedId}
                isReadOnly={isReadOnly}
              />
            ))}
          </SimpleGrid>

          {/* Desktop Table View */}
          <Card
            bg={cardBg}
            border="1px solid"
            borderColor={borderColor}
            display={{ base: 'none', lg: 'block' }}
          >
            <CardBody p={0}>
              <TableContainer>
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Link Name</Th>
                      <Th>Short URL</Th>
                      <Th>UTM Tags</Th>
                      <Th isNumeric>Clicks</Th>
                      <Th>Created</Th>
                      <Th width="50px"></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {linksList.map((link) => (
                      <Tr key={link.link_id} _hover={{ bg: hoverBg }} transition="all 0.2s">
                        <Td>
                          <Box>
                            <Text fontWeight="600">{link.link_name}</Text>
                            <Tooltip label={link.destination_url}>
                              <Text fontSize="xs" color={textSecondary} noOfLines={1} maxW="200px">
                                {link.destination_url}
                              </Text>
                            </Tooltip>
                          </Box>
                        </Td>
                        <Td>
                          <HStack>
                            <Code fontSize="sm">{serverUrl}/l/{link.short_code}</Code>
                            <IconButton
                              icon={<FontAwesomeIcon icon={copied && copiedId === link.link_id ? faCheck : faCopy} />}
                              size="xs"
                              colorScheme={copied && copiedId === link.link_id ? 'green' : 'gray'}
                              variant="ghost"
                              onClick={() => handleCopy(link)}
                              aria-label="Copy URL"
                            />
                          </HStack>
                        </Td>
                        <Td>
                          <HStack spacing={1} flexWrap="wrap">
                            {link.utm_source && (
                              <Badge colorScheme="blue" borderRadius="full" fontSize="xs">
                                {link.utm_source}
                              </Badge>
                            )}
                            {link.utm_medium && (
                              <Badge colorScheme="purple" borderRadius="full" fontSize="xs">
                                {link.utm_medium}
                              </Badge>
                            )}
                            {link.utm_campaign && (
                              <Badge colorScheme="green" borderRadius="full" fontSize="xs">
                                {link.utm_campaign}
                              </Badge>
                            )}
                            {!link.utm_source && !link.utm_medium && !link.utm_campaign && (
                              <Text fontSize="xs" color={textSecondary}>-</Text>
                            )}
                          </HStack>
                        </Td>
                        <Td isNumeric fontWeight="500">{formatFullNumber(link.total_clicks || 0)}</Td>
                        <Td>
                          <Text fontSize="sm" color={textSecondary}>
                            {new Date(link.created_at).toLocaleDateString()}
                          </Text>
                        </Td>
                        <Td>
                          <Menu>
                            <MenuButton
                              as={IconButton}
                              icon={<FontAwesomeIcon icon={faEllipsisV} />}
                              variant="ghost"
                              size="sm"
                              borderRadius="lg"
                            />
                            <Portal>
                              <MenuList zIndex={9999} py={2} borderRadius="xl">
                                <MenuItem icon={<FontAwesomeIcon icon={faChartBar} />} onClick={() => handleViewStats(link)} mx={2} borderRadius="lg" w="calc(100% - 16px)">
                                  View Stats
                                </MenuItem>
                                <MenuItem
                                  icon={<FontAwesomeIcon icon={faEdit} />}
                                  onClick={() => protectAction(() => handleEdit(link))}
                                  isDisabled={isReadOnly}
                                  mx={2}
                                  borderRadius="lg"
                                  w="calc(100% - 16px)"
                                >
                                  Edit
                                </MenuItem>
                                <MenuItem icon={<FontAwesomeIcon icon={faExternalLinkAlt} />} as="a" href={`${serverUrl}/l/${link.short_code}`} target="_blank" mx={2} borderRadius="lg" w="calc(100% - 16px)">
                                  Test Link
                                </MenuItem>
                                <MenuItem
                                  icon={<FontAwesomeIcon icon={faTrash} />}
                                  color={isReadOnly ? 'gray.400' : 'red.500'}
                                  onClick={() => protectAction(() => handleDeleteClick(link))}
                                  isDisabled={isReadOnly}
                                  mx={2}
                                  borderRadius="lg"
                                  w="calc(100% - 16px)"
                                >
                                  Delete
                                </MenuItem>
                              </MenuList>
                            </Portal>
                          </Menu>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </CardBody>
          </Card>
        </>
      ) : (
        <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
          <CardBody textAlign="center" py={12}>
            <Box
              w={16}
              h={16}
              mx="auto"
              mb={4}
              borderRadius="full"
              bg={emptyIconBg}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <FontAwesomeIcon icon={faLink} size="xl" color="var(--chakra-colors-brand-500)" />
            </Box>
            <Heading size="md" mb={2}>No links yet</Heading>
            <Text color={textSecondary} mb={6}>
              Create your first trackable link to start monitoring clicks.
            </Text>
            <Button
              leftIcon={<FontAwesomeIcon icon={faPlus} />}
              colorScheme="brand"
              borderRadius="xl"
              onClick={() => protectAction(onCreateOpen)}
              isDisabled={isReadOnly}
              title={isReadOnly ? 'License required to create links' : undefined}
            >
              Create Your First Link
            </Button>
          </CardBody>
        </Card>
      )}

      {/* Modals */}
      <LinkModal
        isOpen={isCreateOpen}
        onClose={onCreateClose}
        onSubmit={handleCreateSubmit}
        isLoading={isSubmitting}
      />

      <LinkModal
        isOpen={isEditOpen}
        onClose={onEditClose}
        onSubmit={handleEditSubmit}
        link={selectedLink}
        isLoading={isSubmitting}
      />

      <LinkStatsModal
        isOpen={isStatsOpen}
        onClose={onStatsClose}
        link={selectedLink}
      />

      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={handleDeleteConfirm}
        title="Link"
        itemName={selectedLink?.link_name}
        isLoading={isSubmitting}
      />
    </Box>
  );
}

export default LinksPage;
